import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStore } from '../../lib/hooks/useStore';
import { FieldType } from '../../lib/typegen/field-type';
import { Form } from '../../lib/typegen/form';
import { Field } from '../../lib/typegen/field';
import { KeyValue } from '../../lib/typegen/key-value';
import GenericButton from '../button-generic';
import style from './style.module.scss';
import { LookupType } from '../../lib/typegen/lookup-type';
import ScanBarcode from '../scan-barcode';
import FormInput from '../form-input';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import { AccountLookupRequestType } from '../../lib/typegen/account-lookup-request-type';
import { formatPhoneNumber, cleanPhoneNumber } from '../../lib/helpers/phoneFormatter';
import { getCISBehavior } from '../../lib/integrations';
import { ACCOUNT_NUMBER_FIELD_NAME } from '../../lib/constants/field-names';
import logger from '../../lib/helpers/logger';

interface LookupFormProps {
	type: LookupType;
	requestType: AccountLookupRequestType;
	title: string;
	form?: Form | null;
	handleNavigation: (pageId: string, params?: KeyValue[]) => void;
	submitLabel?: string | null;
	defaultValues?: Record<string, string>;
}

export default function LookupForm({ type, requestType, form, title, submitLabel, handleNavigation, defaultValues, ...props }: LookupFormProps) {
	const {
		uiStore: { setTitle },
		configStore: { getString, allowMultipleAccountSelection, cisType },
		accountStore: { lookupAccount, clearLastLookupResponse: clearState, clearCart },
	} = useStore();

	const cisBehavior = getCISBehavior(cisType);
	const accountPattern = cisBehavior.accountNumberPattern;

	const isAccountNumberField = (field: Field) =>
		!!accountPattern && field.type === FieldType.Text && (field.isAccountNumberField === true || field.name === ACCOUNT_NUMBER_FIELD_NAME);

	const defaultValuesRef = useRef(defaultValues);
	defaultValuesRef.current = defaultValues;
	const cisBehaviorRef = useRef(cisBehavior);
	cisBehaviorRef.current = cisBehavior;
	const cisTypeRef = useRef(cisType);
	cisTypeRef.current = cisType;

	// State to track formatted phone number values
	const [phoneValues, setPhoneValues] = useState<Record<string, string>>({});
	const [maskedValues, setMaskedValues] = useState<Record<string, string>>({});

	// simple types for form values
	type LookupFormValues = Record<string, string>;

	const requiredMessage = 'required';

	const {
		handleSubmit,
		register,
		setValue,
		watch,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<LookupFormValues>({
		mode: 'onSubmit',
		reValidateMode: 'onChange',
		defaultValues: defaultValues,
	});

	// Watch all form values efficiently
	const formValues = watch();

	useEffect(() => {
		setTitle(getString(title));
		clearState();
		if (!allowMultipleAccountSelection) clearCart();

		// Inline predicate so this effect never depends on the render-scoped helper (clearCart trap).
		const matchesAccountField = (field: Field) =>
			!!accountPattern && field.type === FieldType.Text && (field.isAccountNumberField === true || field.name === ACCOUNT_NUMBER_FIELD_NAME);

		// Register phone fields manually since they're controlled
		form?.fields?.forEach((field) => {
			if (field.type === FieldType.Tel) {
				register(field.name, { required: requiredMessage });
			} else if (matchesAccountField(field) && !defaultValuesRef.current?.[field.name]) {
				register(field.name, {
					required: requiredMessage,
					validate: (value) => cisBehaviorRef.current.validateAccountNumber?.(value ?? '') || getString('invalid-account-number'),
				});
			}
		});

		if (accountPattern && form?.fields && !form.fields.some(matchesAccountField)) {
			const fieldNames = form.fields.map((f) => f.name).join(', ') || '(none)';
			logger.logCIS.warn(
				{},
				`Account mask pattern set for CIS type ${cisTypeRef.current} but no matching account-number field. Fields present: ${fieldNames}`
			);
		}

		return () => setTitle('');
	}, [setTitle, title, getString, clearState, form?.fields, register, allowMultipleAccountSelection, clearCart, accountPattern]);

	useEffect(() => {
		if (defaultValues) {
			reset(defaultValues);

			// Focus the first field that doesn't have a default value
			setTimeout(() => {
				const firstEmptyField = form?.fields?.find((field) => !defaultValues[field.name]);
				if (firstEmptyField) {
					const input = document.querySelector<HTMLInputElement>(`input[name="${firstEmptyField.name}"]`);
					input?.focus();
				}
			}, 150);
		}
	}, [defaultValues, reset, form?.fields]);

	const onSubmit = async (data: LookupFormValues) => {
		const cleanedData = { ...data };
		form?.fields?.forEach((field) => {
			if (cleanedData[field.name]) {
				cleanedData[field.name] = cleanedData[field.name].trim();

				if (field.type === FieldType.Tel) {
					cleanedData[field.name] = cleanPhoneNumber(cleanedData[field.name]);
				}
			}
		});

		await lookupAccount(requestType, cleanedData, form);
		handleNavigation('select-accounts');
	};

	const fieldTypes: Record<FieldType, 'text' | 'tel' | 'email' | 'number'> = {
		[FieldType.Email]: 'email',
		[FieldType.Tel]: 'tel',
		[FieldType.Text]: 'text',
		// Deliberately 'text', not 'number': <input type='number'> resets its own .value to '' the
		// moment the typed text isn't a valid number (e.g. '123-4'), stranding react-hook-form with
		// an empty value while the invalid characters stay visible. inputMode='numeric' below keeps
		// the numeric keypad. Matches payor-information-form/index.tsx:165.
		[FieldType.Number]: 'text',
	};

	// Keyed off the *configured* FieldType rather than the rendered input type, so FieldType.Number
	// keeps dir='ltr' now that it renders as a text input. Account numbers, phone numbers and email
	// addresses read left-to-right regardless of UI language.
	const inputDir = (fieldType: FieldType) =>
		fieldType === FieldType.Tel || fieldType === FieldType.Email || fieldType === FieldType.Number ? 'ltr' : undefined;

	if (type === LookupType.Barcode) {
		return <ScanBarcode handleNavigation={handleNavigation} />;
	}

	if (type === LookupType.BarcodeWithLastName && !defaultValues) {
		return <ScanBarcode handleNavigation={handleNavigation} lookupType={LookupType.BarcodeWithLastName} />;
	}

	return (
		<form {...props} className={style['form']} onSubmit={handleSubmit(onSubmit)} noValidate>
			<div className={style.form__fields}>
				{form?.fields?.map((field, i) => {
					const inputType = fieldTypes[field.type] ?? 'text';
					const isPhoneField = field.type === FieldType.Tel;

					if (isPhoneField) {
						return (
							<fieldset key={i} className={[style.form__fieldset, style.fieldset].join(' ')}>
								<FormInput
									type={inputType}
									name={field.name}
									dir='ltr'
									placeholder={getString(field.label)}
									error={errors[field.name]?.message as string | undefined}
									value={phoneValues[field.name] || ''}
									onChange={(e) => {
										const formatted = formatPhoneNumber(e.target.value);
										setPhoneValues((prev) => ({ ...prev, [field.name]: formatted }));
										setValue(field.name, formatted, { shouldValidate: true });
									}}
								/>
							</fieldset>
						);
					}

					if (isAccountNumberField(field) && !defaultValues?.[field.name]) {
						return (
							<fieldset key={i} className={[style.form__fieldset, style.fieldset].join(' ')}>
								{/* No error prop here on purpose: an incomplete mask disables the submit button
								    instead of showing a message under a half-typed account number. */}
								<FormInput
									type='text'
									name={field.name}
									placeholder={getString(field.label)}
									maxLength={accountPattern!.length}
									value={maskedValues[field.name] ?? ''}
									onChange={(e) => {
										const previous = maskedValues[field.name] ?? '';
										const raw = e.target.value;
										const formatted = cisBehavior.formatAccountNumber!(raw, raw.length < previous.length);
										setMaskedValues((prev) => ({ ...prev, [field.name]: formatted }));
										setValue(field.name, formatted, { shouldValidate: !!errors[field.name] });
									}}
								/>
							</fieldset>
						);
					}

					return (
						<fieldset key={i} className={[style.form__fieldset, style.fieldset].join(' ')}>
							<FormInput
								{...register(field.name, { required: requiredMessage })}
								type={inputType}
								name={field.name}
								dir={inputDir(field.type)}
								inputMode={field.type === FieldType.Number ? 'numeric' : undefined}
								placeholder={getString(field.label)}
								error={errors[field.name]?.message as string | undefined}
							/>
						</fieldset>
					);
				})}
			</div>
			<GenericButton
				icon={AppIconType.AcceptSolid}
				className={style.form__submit}
				type='submit'
				disabled={
					isSubmitting ||
					!form?.fields?.every((field) => {
						const value = formValues[field.name] || '';
						if (value.trim().length === 0) return false;

						// Masked account fields must also satisfy the mask. Any CIS without a pattern
						// never reaches this branch, so its submit button behaves exactly as before.
						if (isAccountNumberField(field) && !defaultValues?.[field.name]) {
							return cisBehavior.validateAccountNumber?.(value) !== false;
						}

						return true;
					})
				}
				innerText={getString(submitLabel ?? 'done')}
				hasGlow={true}
				isGrowHorizontally={false}
			/>
		</form>
	);
}
